import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import Welcome from "@/pages/Welcome";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Practice from "@/pages/Practice";
import Briefing from "@/pages/Briefing";
import Conversation from "@/pages/Conversation";
import Feedback from "@/pages/Feedback";
import Progress from "@/pages/Progress";
import MyPhrases from "@/pages/MyPhrases";
import Settings from "@/pages/Settings";
import Help from "@/pages/Help";
import Admin from "@/pages/Admin";
import CreateSituation from "@/pages/CreateSituation";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { Toaster } from "@/components/ui/sonner";

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const RequireOnboarded = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
};

function GuestTokenBootstrap() {
  useEffect(() => {
    if (!localStorage.getItem("wpe_guest_token")) {
      import("@/lib/api").then(({ default: client }) =>
        client.post("/guest/token").then((r) => {
          const t = r.data?.guest_token;
          if (t) localStorage.setItem("wpe_guest_token", t);
        }).catch(() => {}),
      );
    }
  }, []);
  return null;
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <GuestTokenBootstrap />
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
            <Route path="/home" element={<RequireOnboarded><Home /></RequireOnboarded>} />
            <Route path="/practice" element={<RequireOnboarded><Practice /></RequireOnboarded>} />
            <Route path="/create" element={<RequireOnboarded><CreateSituation /></RequireOnboarded>} />
            <Route path="/dashboard" element={<Navigate to="/home" replace />} />
            <Route path="/briefing/:scenarioId" element={<Briefing />} />
            <Route path="/session/:sessionId" element={<Conversation />} />
            <Route path="/feedback/:sessionId" element={<Feedback />} />
            <Route path="/phrases" element={<RequireAuth><MyPhrases /></RequireAuth>} />
            <Route path="/progress" element={<RequireAuth><Progress /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
            <Route path="/help" element={<Help />} />
            <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-center" />
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
